"use client";

import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";
import { AlertCircle, Globe } from "lucide-react";

import DesktopPanel from "@/components/desktop/DesktopPanel";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import IconTile from "@/components/ui/icon-tile";
import { useDesktopBridge } from "@/hooks/useDesktopShellWindow";
import { getSafeCallbackUrl } from "@/lib/auth/safe-callback-url";
import { GUIDE_DESKTOP_PATH } from "@/lib/guide/desktop-shell";

// The desktop app's sign-in: no form. The button sends the rep to their
// browser, where they sign in as on any day; the browser hands the app a
// single-use proof (lib/auth/desktop-pairing.js), and this page trades it
// for the same cookie session a browser gets, then goes where the window
// was headed — Guide, unless the sign-in redirect said otherwise.

const STATUS_COPY = {
  idle: null,
  waiting: "Waiting for your browser…",
  signingIn: "Signing you in…",
};

export default function DesktopSignIn() {
  const bridge = useDesktopBridge();
  const router = useRouter();
  const searchParams = useSearchParams();
  const destination = getSafeCallbackUrl(searchParams.get("callbackUrl"), GUIDE_DESKTOP_PATH);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!bridge) return undefined;
    return bridge.onAuthHandoff(async ({ token, verifier }) => {
      setStatus("signingIn");
      setError("");
      const result = await signIn("credentials", {
        redirect: false,
        desktopProof: token,
        codeVerifier: verifier,
      });
      if (result?.error) {
        setStatus("idle");
        setError(result.error);
        return;
      }
      router.replace(destination);
      router.refresh();
    });
  }, [bridge, destination, router]);

  const begin = async () => {
    setStatus("waiting");
    setError("");
    await bridge.signIn();
  };

  return (
    <DesktopPanel className="flex flex-col justify-center">
      <div className="mx-auto w-full max-w-sm">
        <IconTile icon={Globe} size="lg" tone="brand" />
        <h1 className="mt-5 text-2xl font-semibold tracking-tight text-gray-900">Sign in to Guide</h1>
        <p className="mt-2 text-sm leading-relaxed text-gray-600">
          Sign in opens in your browser. Come back here once it’s done.
        </p>

        {error && (
          <Alert variant="destructive" className="mt-6">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            {error}
          </Alert>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-4">
          <Button size="lg" className="rounded-full" disabled={!bridge || status === "signingIn"} onClick={begin}>
            {status === "waiting" ? "Open your browser again" : "Sign in with your browser"}
          </Button>
          {STATUS_COPY[status] && (
            <p className="text-sm text-gray-500" role="status">
              {STATUS_COPY[status]}
            </p>
          )}
        </div>
        {!bridge && (
          <p className="mt-4 text-sm text-gray-500">Open this page in the Salesably Guide app to sign in.</p>
        )}
      </div>
    </DesktopPanel>
  );
}
